import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { trackViewCart } from '../utils/analytics';
import { 
  TrashIcon, 
  PlusIcon, 
  MinusIcon, 
  CreditCardIcon, 
  ExclamationTriangleIcon,
  ShoppingBagIcon,
  HeartIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

function CartPage() {
  const { cartItems, removeFromCart, updateQuantity, cartItemCount } = useCart();
  const [categoryDiscounts, setCategoryDiscounts] = useState([]);
  const [categoryDiscountLoading, setCategoryDiscountLoading] = useState(false);
  const [packageDeals, setPackageDeals] = useState([]);
  const [packageDealsLoading, setPackageDealsLoading] = useState(false);
  const [specialDayDiscounts, setSpecialDayDiscounts] = useState([]);
  const [specialDayLoading, setSpecialDayLoading] = useState(false);
  
  // Flash Sale indirimleri için state'ler
  const [flashSaleDiscounts, setFlashSaleDiscounts] = useState([]);
  const [flashSaleLoading, setFlashSaleLoading] = useState(false);
  
  // Sepet kampanyaları için state'ler
  const [cartCampaigns, setCartCampaigns] = useState([]);
  const [cartCampaignLoading, setCartCampaignLoading] = useState(false);
  
  // Paket kampanyası uyarısı için state'ler
  const [packageCampaignInfo, setPackageCampaignInfo] = useState(null);
  const [packageCampaignLoading, setPackageCampaignLoading] = useState(false);

  // Sepet kampanyalarını kontrol et (YENİ: Birleşik kategori indirimi API'si kullanıyor)
  const checkCartDiscounts = async () => {
    if (!cartItems || cartItems.length === 0) {
      setCategoryDiscounts([]);
      return;
    }

    console.log('🛒 Cart: Kategori indirim kontrol ediliyor:', {
      itemCount: cartItems.length,
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        category: item.category,
        quantity: item.quantity
      }))
    });

    setCategoryDiscountLoading(true);
    try {
      // YENİ: Birleşik kategori indirim API'si kullan
      const response = await api.post('/api/campaigns/check-unified-category-discounts', {
        cartItems: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.variant?.price_override || item.price,
          category: item.category,
          quantity: item.quantity,
          variant: item.variant
        }))
      });

      console.log('📥 Cart: Birleşik kategori indirimi API yanıtı:', response);
      console.log('📊 Cart: API yanıt detayları:', {
        success: response.success,
        hasDiscounts: response.data?.hasDiscounts,
        applicableDiscounts: response.data?.applicableDiscounts,
        totalDiscount: response.data?.totalDiscount,
        method: response.data?.method
      });

      if (response.success && response.data.hasDiscounts) {
        setCategoryDiscounts(response.data.applicableDiscounts);
        console.log('✅ Cart: Kategori indirimleri bulundu:', {
          count: response.data.applicableDiscounts.length,
          method: response.data.method,
          totalDiscount: response.data.totalDiscount
        });
      } else {
        console.log('❌ Cart: Kategori indirimi bulunamadı:', {
          success: response.success,
          hasDiscounts: response.data?.hasDiscounts,
          method: response.data?.method
        });
        setCategoryDiscounts([]);
      }
    } catch (error) {
      console.error('❌ Cart: Kategori indirimi kontrol hatası:', error);
      
      // Fallback: Eski cart discount API'yi dene
      console.log('🔄 Cart: Fallback - Eski cart discount API deneniyor...');
      try {
        const cartTotal = cartItems.reduce((total, item) => {
          const price = item.variant?.price_override || item.price;
          return total + price * item.quantity;
        }, 0);
        
        const fallbackResponse = await api.post('/api/campaigns/check-cart-discounts', {
          cartTotal: cartTotal,
          cartItems: cartItems
        });
        
        if (fallbackResponse.success && fallbackResponse.data.hasDiscounts) {
          setCategoryDiscounts(fallbackResponse.data.applicableDiscounts);
          console.log('✅ Cart: Fallback başarılı:', fallbackResponse.data.applicableDiscounts.length);
        } else {
          setCategoryDiscounts([]);
        }
      } catch (fallbackError) {
        console.error('❌ Cart: Fallback de başarısız:', fallbackError);
        setCategoryDiscounts([]);
      }
    } finally {
      setCategoryDiscountLoading(false);
    }
  };

  // Kategori indirimlerinin toplam tutarını hesapla
  const calculateCategoryDiscount = () => {
    if (!categoryDiscounts || categoryDiscounts.length === 0) return 0;
    return categoryDiscounts.reduce((total, discount) => {
      // Birleşik API kullanırken totalCampaignDiscount tercih edilir
      const amount = discount.totalCampaignDiscount || discount.discountAmount || 0;
      return total + amount;
    }, 0);
  };

  // Paket kampanyası bilgisini kontrol et (uyarı için)
  const checkPackageCampaignInfo = async () => {
    if (!cartItems || cartItems.length === 0) {
      setPackageCampaignInfo(null);
      return;
    }

    console.log('📦 Cart: Paket kampanyası bilgisi kontrol ediliyor...');
    
    setPackageCampaignLoading(true);
    try {
      const response = await api.get('/api/campaigns/active-package-campaigns');
      
      console.log('📥 Cart: Paket kampanyası bilgisi API yanıtı:', response);

      if (response.success && response.data && response.data.length > 0) {
        const activeCampaign = response.data[0]; // İlk aktif kampanyayı al
        
        // Sepetteki toplam ürün sayısını hesapla
        const totalQuantity = cartItems.reduce((total, item) => total + item.quantity, 0);
        
        // Kampanya koşullarını kontrol et
        const buyCount = activeCampaign.package_buy_count || 2;
        const payCount = activeCampaign.package_pay_count || 1;
        const neededForCampaign = buyCount - totalQuantity;
        
        console.log('📊 Cart: Paket kampanyası analizi:', {
          campaignName: activeCampaign.name,
          buyCount,
          payCount,
          totalQuantity,
          neededForCampaign,
          canApply: totalQuantity >= buyCount
        });
        
        if (neededForCampaign > 0) {
          // Kampanya için daha fazla ürün gerekiyor
          setPackageCampaignInfo({
            campaign: activeCampaign,
            neededForCampaign,
            totalQuantity,
            buyCount,
            payCount,
            canApply: false
          });
        } else {
          // Kampanya uygulanabilir
          setPackageCampaignInfo({
            campaign: activeCampaign,
            neededForCampaign: 0,
            totalQuantity,
            buyCount,
            payCount,
            canApply: true
          });
        }
      } else {
        console.log('❌ Cart: Aktif paket kampanyası bulunamadı');
        setPackageCampaignInfo(null);
      }
    } catch (error) {
      console.error('❌ Cart: Paket kampanyası bilgisi kontrol hatası:', error);
      setPackageCampaignInfo(null);
    } finally {
      setPackageCampaignLoading(false);
    }
  };

  // Paket kampanyalarını kontrol et
  const checkPackageDeals = async () => {
    if (!cartItems || cartItems.length === 0) {
      setPackageDeals([]);
      return;
    }

    console.log('📦 Cart: Paket kampanyası kontrol ediliyor:', {
      itemCount: cartItems.length,
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity
      }))
    });

    setPackageDealsLoading(true);
    try {
      const response = await api.post('/api/campaigns/check-package-deals', {
        cartItems: cartItems
      });

      console.log('📥 Cart: Paket kampanyası API yanıtı:', response);

      if (response.success && response.data.hasDiscounts) {
        setPackageDeals(response.data.applicableDiscounts);
        console.log('✅ Cart: Paket kampanyaları bulundu:', response.data.applicableDiscounts.length);
      } else {
        console.log('❌ Cart: Paket kampanyası bulunamadı');
        setPackageDeals([]);
      }
    } catch (error) {
      console.error('❌ Cart: Paket kampanyası kontrol hatası:', error);
      setPackageDeals([]);
    } finally {
      setPackageDealsLoading(false);
    }
  };

  // Paket kampanyalarının toplam tutarını hesapla
  const calculatePackageDiscount = () => {
    if (!packageDeals || packageDeals.length === 0) return 0;
    return packageDeals.reduce((total, deal) => total + deal.discountAmount, 0);
  };

  // Özel gün kampanyalarını kontrol et
  const checkSpecialDayDiscounts = async () => {
    if (!cartItems || cartItems.length === 0) {
      setSpecialDayDiscounts([]);
      return;
    }

    const cartTotal = cartItems.reduce((total, item) => {
      const price = item.variant?.price_override || item.price;
      return total + price * item.quantity;
    }, 0);

    console.log('🎉 Cart: Özel gün kampanyası kontrol ediliyor:', {
      cartTotal: cartTotal,
      itemCount: cartItems.length
    });

    setSpecialDayLoading(true);
    try {
      const response = await api.post('/api/campaigns/check-special-day-discounts', {
        cartItems: cartItems,
        cartTotal: cartTotal,
        userInfo: null // Kullanıcı bilgisi varsa buraya eklenebilir
      });

      console.log('📥 Cart: Özel gün kampanyası API yanıtı:', response);

      if (response.success && response.data.hasDiscounts) {
        setSpecialDayDiscounts(response.data.applicableDiscounts);
        console.log('✅ Cart: Özel gün kampanyaları bulundu:', response.data.applicableDiscounts.length);
      } else {
        console.log('❌ Cart: Özel gün kampanyası bulunamadı');
        setSpecialDayDiscounts([]);
      }
    } catch (error) {
      console.error('❌ Cart: Özel gün kampanyası kontrol hatası:', error);
      setSpecialDayDiscounts([]);
    } finally {
      setSpecialDayLoading(false);
    }
  };

  // Özel gün kampanyalarının toplam tutarını hesapla
  const calculateSpecialDayDiscount = () => {
    if (!specialDayDiscounts || specialDayDiscounts.length === 0) return 0;
    return specialDayDiscounts.reduce((total, discount) => total + discount.discountAmount, 0);
  };

  // Flash Sale kontrolü
  const checkFlashSaleDiscounts = async () => {
    if (!cartItems || cartItems.length === 0) {
      setFlashSaleDiscounts([]);
      return;
    }

    console.log('⚡ Cart: Flash Sale kontrol ediliyor:', cartItems.length, 'ürün');
    setFlashSaleLoading(true);

    try {
      const response = await api.get('/api/flash-sale/active');
      
      if (response.success && response.data && response.data.length > 0) {
        const applicableDiscounts = [];
        
        // Her flash sale kampanyasını kontrol et
        for (const flashSale of response.data) {
          if (!flashSale.products || flashSale.products.length === 0) continue;
          
          // Sepetteki ürünlerden bu flash sale'de olanları bul
          const matchingItems = cartItems.filter(cartItem => 
            flashSale.products.some(p => String(p.id) === String(cartItem.id))
          );
          
          if (matchingItems.length > 0) {
            let totalDiscount = 0;
            
            matchingItems.forEach(cartItem => {
              const productInSale = flashSale.products.find(p => String(p.id) === String(cartItem.id));
              const toNum = (v) => {
                const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
                return isNaN(n) ? 0 : n;
              };
              const itemPrice = toNum(cartItem.variant?.price_override != null ? cartItem.variant.price_override : cartItem.price);
              const discountValueRaw = (productInSale && productInSale.custom_discount_value != null)
                ? productInSale.custom_discount_value
                : flashSale.discount_value;
              const discountValue = toNum(discountValueRaw);
              
              let itemDiscount = 0;
              if (flashSale.discount_type === 'percentage') {
                if (discountValue > 0) {
                  itemDiscount = (itemPrice * discountValue) / 100;
                } else if (toNum(productInSale?.discount_percentage) > 0) {
                  itemDiscount = (itemPrice * toNum(productInSale.discount_percentage)) / 100;
                } else if (toNum(productInSale?.discounted_price) > 0) {
                  itemDiscount = Math.max(0, itemPrice - toNum(productInSale.discounted_price));
                }
              } else {
                itemDiscount = discountValue > 0
                  ? discountValue
                  : Math.max(0, itemPrice - toNum(productInSale?.discounted_price));
              }
              
              totalDiscount += toNum(itemDiscount) * (cartItem.quantity || 1);
            });
            
            applicableDiscounts.push({
              campaignId: flashSale.id,
              campaignName: flashSale.name,
              badgeText: flashSale.badge_text || `FLASH %${flashSale.discount_value}`,
              badgeColor: flashSale.badge_color || '#ff4444',
              discountAmount: totalDiscount,
              discountType: flashSale.discount_type,
              discountValue: flashSale.discount_value,
              matchingItems: matchingItems.length
            });
          }
        }
        
        setFlashSaleDiscounts(applicableDiscounts);
        console.log('✅ Cart: Flash Sale indirimleri bulundu:', applicableDiscounts.length);
      } else {
        console.log('❌ Cart: Aktif Flash Sale bulunamadı');
        setFlashSaleDiscounts([]);
      }
    } catch (error) {
      console.error('❌ Cart: Flash Sale kontrol hatası:', error);
      setFlashSaleDiscounts([]);
    } finally {
      setFlashSaleLoading(false);
    }
  };

  // Flash Sale indirimlerinin toplam tutarını hesapla
  const calculateFlashSaleDiscount = () => {
    if (!flashSaleDiscounts || flashSaleDiscounts.length === 0) return 0;
    return flashSaleDiscounts.reduce((total, discount) => total + discount.discountAmount, 0);
  };

  // Sepet kampanyalarını kontrol et
  const checkCartCampaigns = async () => {
    if (!cartItems || cartItems.length === 0) {
      setCartCampaigns([]);
      return;
    }

    const cartTotal = cartItems.reduce((total, item) => {
      const price = item.variant?.price_override || item.price;
      return total + price * item.quantity;
    }, 0);

    console.log('🛒 Cart: Sepet kampanyası kontrol ediliyor:', {
      cartTotal: cartTotal,
      itemCount: cartItems.length
    });

    setCartCampaignLoading(true);
    try {
      const response = await api.post('/api/campaigns/check-cart-discounts', {
        cartTotal: cartTotal,
        cartItems: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.variant?.price_override || item.price,
          category: item.category,
          quantity: item.quantity,
          variant: item.variant
        }))
      });

      console.log('📥 Cart: Sepet kampanyası API yanıtı:', response);

      if (response.success && response.data.hasDiscounts) {
        setCartCampaigns(response.data.applicableDiscounts);
        console.log('✅ Cart: Sepet kampanyaları bulundu:', response.data.applicableDiscounts.length);
      } else {
        console.log('❌ Cart: Sepet kampanyası bulunamadı');
        setCartCampaigns([]);
      }
    } catch (error) {
      console.error('❌ Cart: Sepet kampanyası kontrol hatası:', error);
      setCartCampaigns([]);
    } finally {
      setCartCampaignLoading(false);
    }
  };

  // Sepet kampanyalarının toplam tutarını hesapla
  const calculateCartCampaignDiscount = () => {
    if (!cartCampaigns || cartCampaigns.length === 0) return 0;
    return cartCampaigns.reduce((total, campaign) => total + campaign.discountAmount, 0);
  };

  // Sepet değiştiğinde kampanyaları kontrol et ve GA4 view_cart eventi gönder
  useEffect(() => {
    checkCartDiscounts();
    checkPackageDeals();
    checkPackageCampaignInfo(); // Paket kampanyası bilgisi kontrolü eklendi
    checkSpecialDayDiscounts();
    checkFlashSaleDiscounts();
    checkCartCampaigns();
    
    // GA4: Sepet görüntüleme eventi
    if (cartItems && cartItems.length > 0) {
      trackViewCart(cartItems.map(item => ({
        product: {
          id: item.id,
          name: item.name,
          price: item.variant?.price_override || item.price,
          category: item.category
        },
        quantity: item.quantity,
        selected_size: item.variant?.size || item.variant?.color || ''
      })));
    }
  }, [cartItems]);

  const FREE_SHIPPING_LIMIT = 2000;
  
  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.variant.price_override != null ? item.variant.price_override : item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const remainingForFreeShipping = FREE_SHIPPING_LIMIT - subtotal;
  const isEligibleForFreeShipping = subtotal >= FREE_SHIPPING_LIMIT;

  // Toplam desi hesapla
  const calculateTotalDesi = () => {
    if (!cartItems || cartItems.length === 0) return 0;
    
    let totalDesi = 0;
    cartItems.forEach(item => {
      const itemDesi = item.desi || 0.5; // Varsayılan 0.5 desi
      totalDesi += (item.quantity || 1) * itemDesi;
    });
    
    return Math.ceil(totalDesi * 10) / 10; // 1 ondalık basamağa yuvarla
  };
  
  // YENİ: Sepette stok dışı ürün olup olmadığını kontrol et
  const isAnyItemOutOfStock = cartItems.some(item => item.quantity > item.variant.stock);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Modern Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green rounded-full mb-4 shadow-lg">
            <ShoppingBagIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-3 tracking-tight">Sepetim</h1>
          <p className="text-text-light text-lg max-w-md mx-auto">
            {cartItems.length > 0 ? `${cartItemCount} ürün sepetinizde` : 'Sepetiniz boş'}
          </p>
        </div>
        {cartItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <ShoppingBagIcon className="w-16 h-16 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-600 mb-4">Sepetiniz Boş</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Henüz sepetinizde ürün bulunmuyor. Hemen alışverişe başlayın ve favori ürünlerinizi keşfedin!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/" 
                className="inline-flex items-center justify-center bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-xl hover:shadow-2xl"
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                Alışverişe Başla
              </Link>
              <Link 
                to="/favorites" 
                className="inline-flex items-center justify-center bg-white hover:bg-gray-50 text-brand-green font-bold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl border-2 border-brand-green"
              >
                <HeartIcon className="w-5 h-5 mr-2" />
                Favorilerim
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Ürünler Listesi */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 backdrop-blur-sm">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-brand-green rounded-xl flex items-center justify-center mr-3">
                      <ShoppingBagIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-text-dark">Sepetinizdeki Ürünler</h2>
                      <p className="text-text-light text-sm">{cartItemCount} ürün</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  {cartItems.map((item, index) => {
                    // YENİ: Stok kontrolü
                    const hasEnoughStock = item.quantity <= item.variant.stock;
                    const itemPrice = item.variant.price_override != null ? item.variant.price_override : item.price;
                    
                    return (
                      <div 
                        key={item.cartId} 
                        className={`group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] ${!hasEnoughStock ? 'ring-2 ring-red-200 bg-red-50' : ''}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        {/* Stok Uyarı Banner */}
                        {!hasEnoughStock && (
                          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 text-sm font-semibold flex items-center">
                            <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                            Stokta sadece {item.variant.stock} adet kaldı!
                          </div>
                        )}
                        
                        <div className="p-6">
                          <div className="flex flex-col md:flex-row gap-6">
                            {/* Ürün Resmi */}
                            <div className="relative">
                              <img 
                                src={item.main_image_url || 'https://placehold.co/150x150'} 
                                alt={item.name} 
                                className="w-32 h-32 object-cover rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-105" 
                              />
                              <div className="absolute -top-2 -right-2 w-8 h-8 bg-brand-green rounded-full flex items-center justify-center shadow-lg">
                                <span className="text-white text-sm font-bold">{item.quantity}</span>
                              </div>
                            </div>
                            
                            {/* Ürün Bilgileri */}
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-brand-green transition-colors">
                                {item.name}
                              </h3>
                              <div className="flex flex-wrap items-center gap-4 mb-4">
                                <div className="flex items-center space-x-2">
                                  <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-300" style={{backgroundColor: item.variant.color?.toLowerCase()}}></span>
                                  <span className="text-sm font-medium text-gray-600">{item.variant.color}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-600">Beden:</span>
                                  <span className="bg-gray-100 px-2 py-1 rounded-lg text-sm font-semibold">{item.variant.size}</span>
                                </div>
                              </div>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-2xl font-bold text-brand-green">
                                    ₺{(itemPrice * item.quantity).toFixed(2)}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    ₺{itemPrice.toFixed(2)} / adet
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Kontroller */}
                            <div className="flex flex-col justify-between items-end gap-4">
                              {/* Miktar Kontrolü */}
                              <div className="flex items-center bg-gray-50 rounded-2xl border-2 border-gray-200 hover:border-brand-green transition-colors">
                                <button 
                                  onClick={() => updateQuantity(item.cartId, item.quantity - 1)} 
                                  className="p-3 text-gray-600 hover:text-brand-green hover:bg-white rounded-l-2xl transition-all duration-200"
                                  disabled={item.quantity <= 1}
                                >
                                  <MinusIcon className='w-5 h-5'/>
                                </button>
                                <span className="px-6 py-3 font-bold text-lg min-w-[60px] text-center">{item.quantity}</span>
                                <button 
                                  onClick={() => updateQuantity(item.cartId, item.quantity + 1)} 
                                  className="p-3 text-gray-600 hover:text-brand-green hover:bg-white rounded-r-2xl transition-all duration-200"
                                  disabled={!hasEnoughStock && item.quantity >= item.variant.stock}
                                >
                                  <PlusIcon className='w-5 h-5'/>
                                </button>
                              </div>
                              
                              {/* Kaldır Butonu */}
                              <button 
                                onClick={() => removeFromCart(item.cartId)} 
                                className="flex items-center gap-2 text-red-500 hover:text-white hover:bg-red-500 px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105 border border-red-200 hover:border-red-500"
                              >
                                <TrashIcon className="w-4 h-4" />
                                <span className="font-medium">Kaldır</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* Sipariş Özeti */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl shadow-2xl p-8 sticky top-8 border border-gray-100 backdrop-blur-sm">
                <div className="flex items-center mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-green to-brand-green-light rounded-xl flex items-center justify-center shadow-lg mr-4">
                    <CreditCardIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-text-dark">Sipariş Özeti</h2>
                    <p className="text-text-light text-sm">{cartItemCount} ürün</p>
                  </div>
                </div>

                <div className="space-y-6 mb-8">
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-2xl">
                    <span className="text-gray-700 font-medium">Ara Toplam</span>
                    <span className="text-gray-800 font-bold text-lg">₺{subtotal.toFixed(2)}</span>
                  </div>

                  {/* Sepet Kampanyaları */}
                  {categoryDiscounts.length > 0 && (
                    <div className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300 p-4 rounded-2xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                          </div>
                          <span className="text-green-800 font-bold text-sm">
                            Kategori İndirimleri
                          </span>
                        </div>
                        <span className="text-green-700 font-bold">
                          -₺{calculateCategoryDiscount().toFixed(2)}
                        </span>
                      </div>
                      
                      {/* İndirim Detayları */}
                      <div className="space-y-2">
                        {categoryDiscounts.map((discount, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-2 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                {discount.badgeText && (
                                  <span 
                                    className="px-2 py-1 text-xs font-bold text-white rounded-full"
                                    style={{ backgroundColor: discount.badgeColor || '#10b981' }}
                                  >
                                    {discount.badgeText}
                                  </span>
                                )}
                                <span className="text-xs font-medium text-gray-800">
                                  {discount.campaignName}
                                </span>
                              </div>
                              <span className="text-xs font-bold text-green-700">
                                -₺{(discount.totalCampaignDiscount || discount.discountAmount || 0).toFixed(2)}
                              </span>
                            </div>
                            {discount.description && (
                              <p className="text-xs text-gray-600 mt-1">{discount.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className={`flex justify-between items-center p-4 rounded-2xl ${isEligibleForFreeShipping ? 'bg-green-50' : 'bg-orange-50'}`}>
                    <span className="text-gray-700 font-medium">Kargo</span>
                    <div className="flex items-center">
                      {isEligibleForFreeShipping ? (
                        <>
                          <span className="text-green-600 font-bold mr-2">Ücretsiz</span>
                          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        </>
                      ) : (
                        <span className="text-orange-600 font-bold">+ Kargo Ücreti</span>
                      )}
                    </div>
                  </div>

                  {/* Paket Kampanyası Uyarısı - Kampanya uygulanamıyorsa */}
                  {packageCampaignInfo && !packageCampaignInfo.canApply && packageCampaignInfo.neededForCampaign > 0 && (
                    <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 p-4 rounded-2xl mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-purple-500 rounded-lg flex items-center justify-center">
                            <span className="text-white text-sm font-bold">📦</span>
                          </div>
                          <span className="text-purple-800 font-bold text-sm">
                            {packageCampaignInfo.campaign.name}
                          </span>
                        </div>
                        <span className="text-purple-700 font-bold text-sm">
                          {packageCampaignInfo.buyCount} Al {packageCampaignInfo.payCount} Öde
                        </span>
                      </div>
                      
                      <div className="bg-white bg-opacity-60 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-purple-800 font-semibold text-sm">
                              Kampanyadan yararlanmak için:
                            </span>
                          </div>
                          <span className="text-purple-700 font-bold text-sm">
                            {packageCampaignInfo.neededForCampaign} ürün daha ekleyin
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>Sepetinizdeki ürün: {packageCampaignInfo.totalQuantity}</span>
                          <span>Gerekli: {packageCampaignInfo.buyCount}</span>
                        </div>
                        
                        <div className="mt-2 text-xs text-purple-700">
                          💡 Sepete {packageCampaignInfo.neededForCampaign} ürün daha ekleyerek {packageCampaignInfo.buyCount - packageCampaignInfo.payCount} ürün bedava alın!
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Paket Kampanyaları */}
                  {packageDeals.length > 0 && (
                    <div className="bg-gradient-to-r from-orange-100 to-red-100 border-2 border-orange-300 p-4 rounded-2xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-orange-500 rounded-lg flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                          </div>
                          <span className="text-orange-800 font-bold text-sm">
                            Paket Kampanyası
                          </span>
                        </div>
                        <span className="text-orange-700 font-bold">
                          -₺{calculatePackageDiscount().toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Paket Kampanyası Detayları */}
                      <div className="space-y-2">
                        {packageDeals.map((deal, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-2 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span 
                                  className="px-2 py-1 text-xs font-bold text-white rounded-full animate-pulse"
                                  style={{ backgroundColor: deal.badgeColor || '#ff6b35' }}
                                >
                                  {deal.badgeText}
                                </span>
                                <span className="text-xs font-medium text-gray-800">
                                  {deal.campaignName}
                                </span>
                              </div>
                              <span className="text-xs font-bold text-orange-700">
                                -₺{deal.discountAmount.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Etkilenen Ürünler */}
                            {deal.eligibleItems && deal.eligibleItems.length > 0 && (
                              <div className="space-y-1">
                                {deal.eligibleItems.map((item, itemIndex) => (
                                  <div key={itemIndex} className="flex justify-between text-xs text-gray-600">
                                    <span>
                                      {item.productName}
                                      {item.variantInfo && ` (${item.variantInfo})`}
                                      {item.freeItems && ` - ${item.freeItems} adet bedava`}
                                    </span>
                                    <span>-₺{item.itemDiscount.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {deal.description && (
                              <p className="text-xs text-gray-600 mt-1">{deal.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Özel Gün Kampanyaları */}
                  {specialDayDiscounts.length > 0 && (
                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 p-4 rounded-2xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-purple-500 rounded-lg flex items-center justify-center animate-bounce">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                            </svg>
                          </div>
                          <span className="text-purple-800 font-bold text-sm">
                            🎉 Özel Gün Kampanyası
                          </span>
                        </div>
                        <span className="text-purple-700 font-bold">
                          -₺{calculateSpecialDayDiscount().toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Özel Gün Kampanyası Detayları */}
                      <div className="space-y-2">
                        {specialDayDiscounts.map((discount, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-2 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span 
                                  className="px-2 py-1 text-xs font-bold text-white rounded-full animate-pulse"
                                  style={{ backgroundColor: discount.badgeColor || '#8b5cf6' }}
                                >
                                  🎉 {discount.badgeText}
                                </span>
                                <span className="text-xs font-medium text-gray-800">
                                  {discount.campaignName}
                                </span>
                              </div>
                              <span className="text-xs font-bold text-purple-700">
                                -₺{discount.discountAmount.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Hedef Kitle Bilgisi */}
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>
                                {(() => {
                                  const audienceNames = {
                                    'all': 'Tüm müşteriler',
                                    'new_customers': 'Yeni müşteriler',
                                    'returning_customers': 'Sadık müşteriler',
                                    'vip_customers': 'VIP müşteriler'
                                  };
                                  return audienceNames[discount.targetAudience] || 'Tüm müşteriler';
                                })()}
                              </span>
                              <span className="text-purple-600 font-medium">
                                {(() => {
                                  const dayNames = {
                                    'mothers_day': 'Anneler Günü',
                                    'fathers_day': 'Babalar Günü',
                                    'childrens_day': '23 Nisan',
                                    'new_year': 'Yeni Yıl',
                                    'valentines_day': 'Sevgililer Günü',
                                    'back_to_school': 'Okula Dönüş',
                                    'semester_break': 'Sömestr',
                                    'summer_vacation': 'Yaz Tatili',
                                    'report_card_day': 'Karne Günü',
                                    'custom_date': 'Özel Gün'
                                  };
                                  return dayNames[discount.specialDayType] || 'Özel Gün';
                                })()}
                              </span>
                            </div>
                            
                            {discount.description && (
                              <p className="text-xs text-gray-600 mt-1">{discount.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Flash Sale İndirimleri */}
                  {flashSaleDiscounts.length > 0 && (
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-red-600 font-bold text-lg">⚡ Flash Sale İndirimi</span>
                        </div>
                        <span className="text-red-700 font-bold">
                          -₺{calculateFlashSaleDiscount().toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Flash Sale Kampanyası Detayları */}
                      <div className="space-y-2">
                        {flashSaleDiscounts.map((discount, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-2 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span 
                                  className="px-2 py-1 text-xs font-bold text-white rounded-full animate-pulse"
                                  style={{ backgroundColor: discount.badgeColor || '#ff4444' }}
                                >
                                  ⚡ {discount.badgeText}
                                </span>
                                <span className="text-xs font-medium text-gray-800">
                                  {discount.campaignName}
                                </span>
                              </div>
                              <span className="text-xs font-bold text-red-700">
                                -₺{discount.discountAmount.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Flash Sale Detayları */}
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>
                                {discount.matchingItems} ürün Flash Sale'de
                              </span>
                              <span className="text-red-600 font-medium">
                                %{discount.discountValue} indirim
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sepet Kampanyaları */}
                  {cartCampaigns.length > 0 && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-green-600 font-bold text-lg">🛒 Sepet İndirimi</span>
                        </div>
                        <span className="text-green-700 font-bold">
                          -₺{calculateCartCampaignDiscount().toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Sepet Kampanyası Detayları */}
                      <div className="space-y-2">
                        {cartCampaigns.map((campaign, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-2 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span 
                                  className="px-2 py-1 text-xs font-bold text-white rounded-full"
                                  style={{ backgroundColor: campaign.badgeColor || '#10b981' }}
                                >
                                  🛒 {campaign.badgeText || 'SEPET İNDİRİMİ'}
                                </span>
                                <span className="text-xs font-medium text-gray-800">
                                  {campaign.campaignName}
                                </span>
                              </div>
                              <span className="text-xs font-bold text-green-700">
                                -₺{campaign.discountAmount.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Sepet Kampanyası Detayları */}
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>
                                {campaign.minCartAmount && `${campaign.minCartAmount} TL üzeri alışveriş`}
                                {campaign.discountType === 'percentage' ? ` %${campaign.discountValue}` : ` ${campaign.discountValue} TL`} indirim
                              </span>
                              <span className="text-green-600 font-medium">
                                Sepet indirimi uygulandı
                              </span>
                            </div>
                            
                            {campaign.description && (
                              <p className="text-xs text-gray-600 mt-1">{campaign.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ücretsiz Kargo Uyarısı */}
                  {!isEligibleForFreeShipping && (
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                      <div className="flex items-center space-x-3">
                        <ShoppingBagIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="text-blue-800 font-bold">
                            {remainingForFreeShipping.toFixed(2)} ₺ daha alışveriş yapın,
                          </p>
                          <p className="text-blue-600 font-medium">
                            kargo ücretsiz olsun! 🚚
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {isEligibleForFreeShipping && (
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                      <div className="flex items-center space-x-3">
                        <ShoppingBagIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="text-green-800 font-bold">
                            Tebrikler! Ücretsiz kargo kazandınız! 🎉
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-6 rounded-2xl shadow-lg">
                    <div className="flex justify-between items-center text-white">
                      <span className="text-xl font-bold">Toplam</span>
                      <span className="text-3xl font-bold">₺{(subtotal - calculateCategoryDiscount() - calculatePackageDiscount() - calculateSpecialDayDiscount() - calculateFlashSaleDiscount() - calculateCartCampaignDiscount()).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <Link 
                  to={cartItemCount > 0 && !isAnyItemOutOfStock ? "/checkout" : "#"}
                  className={`w-full block text-center bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-5 px-8 rounded-2xl transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] active:scale-[0.98] ${cartItemCount === 0 || isAnyItemOutOfStock ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-center gap-3">
                    <CreditCardIcon className="w-6 h-6" />
                    <span className="text-lg">Siparişi Tamamla</span>
                  </div>
                </Link>
                
                {/* Stok Uyarısı */}
                {isAnyItemOutOfStock && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-2xl">
                    <div className="flex items-center text-red-600">
                      <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                      <span className="text-sm font-semibold">Lütfen ürün adetlerini güncelleyin</span>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 text-center mt-6">
                  Siparişinizi tamamlayarak <span className="text-brand-green font-semibold">Gizlilik Politikası</span> ve <span className="text-brand-green font-semibold">Kullanım Şartları</span>'nı kabul etmiş olursunuz.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CartPage;